import React, { useState, useEffect, useMemo } from 'react';
import FundSearchModal from './FundSearchModal';
import TemplateTextField from './TemplateTextField';

const BAR_COLOURS = ['#072040', '#599BE3', '#f0a500', '#91CF9B', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const StepStrategy = ({
  formData, updateFormData, nextStep, prevStep,
  portfolios, selectedPortfolio, selectPortfolio,
  getFilteredPortfolios, calculateBlend, searchFunds, fetchAssetClasses,
  disabled,
}) => {
  const data = formData.strategy;
  const riskProfile = formData.riskProfile?.selectedProfile;

  const [assetClasses, setAssetClasses] = useState([]);
  const [showFundSearch, setShowFundSearch] = useState(false);
  const [blendLoading, setBlendLoading] = useState(false);

  const handleChange = (field, value) => {
    updateFormData('strategy', { [field]: value });
  };

  // Load asset classes on mount
  useEffect(() => {
    if (assetClasses.length === 0 && fetchAssetClasses) {
      fetchAssetClasses().then(setAssetClasses).catch(console.error);
    }
  }, [assetClasses.length, fetchAssetClasses]);

  // ── Data ──
  const modelWeights = data.modelWeights || {};
  const fundEntries = data.fundEntries || [];
  const acWeights = data.assetClassWeights || {};
  const blendResults = data.blendResults;

  // ── Model portfolio helpers ──
  const modelRows = useMemo(() => {
    return Object.entries(modelWeights).map(([code, weight]) => ({ code, weight }));
  }, [modelWeights]);

  const selectedModelCodes = useMemo(() => new Set(Object.keys(modelWeights)), [modelWeights]);

  // Group portfolios by suite for optgroups
  const portfolioCategories = useMemo(() => {
    const suiteMap = {};
    portfolios.forEach(p => {
      const suite = p.suite || 'Other';
      if (!suiteMap[suite]) suiteMap[suite] = [];
      suiteMap[suite].push(p);
    });
    return Object.entries(suiteMap).map(([label, items]) => ({ label, items }));
  }, [portfolios]);

  const addModelRow = () => {
    // Find first unselected portfolio
    const available = portfolios.find(p => !selectedModelCodes.has(p.portfolio_code));
    if (available) {
      handleChange('modelWeights', { ...modelWeights, [available.portfolio_code]: 0 });
    }
  };

  const removeModelRow = (code) => {
    const updated = { ...modelWeights };
    delete updated[code];
    handleChange('modelWeights', updated);
  };

  const updateModelSelection = (oldCode, newCode) => {
    const updated = { ...modelWeights };
    const weight = updated[oldCode] || 0;
    delete updated[oldCode];
    updated[newCode] = weight;
    handleChange('modelWeights', updated);
  };

  const updateModelWeight = (code, weight) => {
    handleChange('modelWeights', { ...modelWeights, [code]: parseFloat(weight) || 0 });
  };

  // ── Fund helpers ──
  const addFund = (fund) => {
    const existing = fundEntries.find(f => f.tr_code === fund.tr_code);
    if (existing) return;
    handleChange('fundEntries', [...fundEntries, { ...fund, weight: 0 }]);
  };

  const updateFundWeight = (index, weight) => {
    const updated = [...fundEntries];
    updated[index] = { ...updated[index], weight: parseFloat(weight) || 0 };
    handleChange('fundEntries', updated);
  };

  const removeFund = (index) => {
    handleChange('fundEntries', fundEntries.filter((_, i) => i !== index));
  };

  // ── Asset class helpers ──
  const acRows = useMemo(() => {
    return Object.entries(acWeights).map(([name, weight]) => ({ name, weight }));
  }, [acWeights]);

  const selectedAcNames = useMemo(() => new Set(Object.keys(acWeights)), [acWeights]);

  const addAcRow = () => {
    const available = assetClasses.find(ac => !selectedAcNames.has(ac.name));
    if (available) {
      handleChange('assetClassWeights', { ...acWeights, [available.name]: 0 });
    }
  };

  const removeAcRow = (name) => {
    const updated = { ...acWeights };
    delete updated[name];
    handleChange('assetClassWeights', updated);
  };

  const updateAcSelection = (oldName, newName) => {
    const updated = { ...acWeights };
    const weight = updated[oldName] || 0;
    delete updated[oldName];
    updated[newName] = weight;
    handleChange('assetClassWeights', updated);
  };

  const updateAcWeight = (name, weight) => {
    handleChange('assetClassWeights', { ...acWeights, [name]: parseFloat(weight) || 0 });
  };

  // ── Total weight ──
  const tw = useMemo(() => {
    let total = 0;
    Object.values(modelWeights).forEach(w => total += (parseFloat(w) || 0));
    fundEntries.forEach(f => total += (parseFloat(f.weight) || 0));
    Object.values(acWeights).forEach(w => total += (parseFloat(w) || 0));
    return total;
  }, [modelWeights, fundEntries, acWeights]);

  const isValidTotal = Math.abs(tw - 100) < 0.5;

  // ── All allocation items for the stacked bar ──
  const allItems = useMemo(() => {
    const items = [];
    modelRows.forEach(r => {
      const p = portfolios.find(x => x.portfolio_code === r.code);
      if (r.weight > 0) items.push({ label: p?.display_name || r.code, weight: r.weight });
    });
    fundEntries.forEach(f => {
      if (f.weight > 0) items.push({ label: f.fund_name || f.tr_code, weight: f.weight });
    });
    acRows.forEach(r => {
      if (r.weight > 0) {
        const ac = assetClasses.find(a => a.name === r.name);
        items.push({ label: ac ? ac.name.replace(/([A-Z])/g, ' $1').trim() : r.name, weight: r.weight });
      }
    });
    return items;
  }, [modelRows, fundEntries, acRows, portfolios, assetClasses]);

  const handleCalculateBlend = async () => {
    setBlendLoading(true);
    try {
      await calculateBlend();
    } catch (err) {
      console.error('Blend calculation failed:', err);
    } finally {
      setBlendLoading(false);
    }
  };

  // ── Render helpers ──
  const renderStackedBar = () => {
    if (allItems.length === 0) return null;
    return (
      <div className="mb-3">
        <div style={{ display: 'flex', height: '28px', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--light-color)' }}>
          {allItems.map((item, i) => (
            <div
              key={i}
              style={{
                width: `${item.weight}%`,
                backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length],
                transition: 'width 0.3s ease',
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
              }}
              title={`${item.label}: ${item.weight}%`}
            >
              {item.weight >= 8 && (
                <span style={{ color: '#fff', fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  {item.weight}%
                </span>
              )}
            </div>
          ))}
          {tw < 100 && (
            <div style={{ flex: 1, backgroundColor: 'var(--light-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '11px', color: 'var(--gray-color)' }}>
                {(100 - tw).toFixed(0)}% unallocated
              </span>
            </div>
          )}
        </div>
        <div className="d-flex flex-wrap gap-3 mt-2">
          {allItems.map((item, i) => (
            <div key={i} className="d-flex align-items-center">
              <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length], display: 'inline-block', marginRight: 6 }}></span>
              <small className="text-muted">{item.label}</small>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Recommended Strategy</h4>
      <p className="soa-step-subtitle">Build your allocation from model portfolios, individual funds, and asset classes</p>

      {/* ═══ Stacked allocation bar ═══ */}
      {renderStackedBar()}

      {/* ═══ Model Portfolios ═══ */}
      <div className="soa-section-banner">
        <i className="bi bi-pie-chart"></i>
        Model Portfolios
        {riskProfile && <span className="ms-auto small" style={{ opacity: 0.8 }}>Risk profile: {riskProfile}</span>}
      </div>

      <div className="mb-3">
        {modelRows.length > 0 && (
          <div className="table-responsive mb-2">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr style={{ backgroundColor: 'var(--light-color)' }}>
                  <th style={{ width: '50%' }}>Portfolio</th>
                  <th style={{ width: '25%' }}>Allocation (%)</th>
                  <th style={{ width: '15%' }} className="text-center">Weight</th>
                  <th style={{ width: '10%' }}></th>
                </tr>
              </thead>
              <tbody>
                {modelRows.map((row, i) => {
                  const p = portfolios.find(x => x.portfolio_code === row.code);
                  return (
                    <tr key={row.code}>
                      <td>
                        <div className="d-flex align-items-center">
                          <span style={{ width: 4, height: 28, borderRadius: 2, backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length], display: 'inline-block', marginRight: 10, flexShrink: 0 }}></span>
                          <select
                            className="form-select form-select-sm"
                            value={row.code}
                            onChange={(e) => updateModelSelection(row.code, e.target.value)}
                            style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none', paddingLeft: 0 }}
                          >
                            {portfolioCategories.map(cat => {
                              const options = cat.items.filter(
                                p => p.portfolio_code === row.code || !selectedModelCodes.has(p.portfolio_code)
                              );
                              if (options.length === 0) return null;
                              return (
                                <optgroup key={cat.label} label={cat.label}>
                                  {options.map(p => (
                                    <option key={p.portfolio_code} value={p.portfolio_code}>
                                      {p.display_name} — Growth {Math.round((p.growth_exposure || 0) * 100)}%
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })}
                          </select>
                        </div>
                      </td>
                      <td>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="0" max="100"
                            value={row.weight || ''}
                            placeholder="0"
                            onChange={(e) => updateModelWeight(row.code, e.target.value)}
                            style={{ paddingRight: '28px' }}
                          />
                          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-color)', fontSize: '13px', pointerEvents: 'none' }}>%</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <div style={{ width: '100%', height: 6, backgroundColor: 'var(--light-color)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(row.weight || 0, 100)}%`, height: '100%', backgroundColor: BAR_COLOURS[i % BAR_COLOURS.length], borderRadius: 3, transition: 'width 0.3s ease' }}></div>
                        </div>
                      </td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm" onClick={() => removeModelRow(row.code)} title="Remove" style={{ color: '#dc3545', padding: '2px 6px' }}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {modelRows.length === 0 && (
          <div className="text-center py-3 mb-2" style={{ backgroundColor: 'var(--light-color)', borderRadius: '8px' }}>
            <p className="text-muted mb-0 small">No model portfolios added yet</p>
          </div>
        )}

        <div className="text-center mb-1">
          <button type="button" className="btn btn-sm text-white" style={{ backgroundColor: 'var(--primary-color)' }} onClick={addModelRow}
            disabled={selectedModelCodes.size >= portfolios.length}>
            <i className="bi bi-plus-circle me-1"></i>Add Portfolio
          </button>
        </div>
      </div>

      {/* ═══ Individual Funds ═══ */}
      <div className="soa-section-banner">
        <i className="bi bi-search"></i>
        Individual Funds
      </div>

      <div className="mb-3">
        {fundEntries.length > 0 && (
          <div className="table-responsive mb-2">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr style={{ backgroundColor: 'var(--light-color)' }}>
                  <th style={{ width: '40%' }}>Fund</th>
                  <th style={{ width: '15%' }}>Asset Class</th>
                  <th style={{ width: '10%' }}>TER</th>
                  <th style={{ width: '20%' }}>Allocation (%)</th>
                  <th style={{ width: '10%' }} className="text-center">Weight</th>
                  <th style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {fundEntries.map((fund, idx) => {
                  const colourIdx = modelRows.length + idx;
                  return (
                    <tr key={fund.tr_code || idx}>
                      <td>
                        <div className="d-flex align-items-center">
                          <span style={{ width: 4, height: 28, borderRadius: 2, backgroundColor: BAR_COLOURS[colourIdx % BAR_COLOURS.length], display: 'inline-block', marginRight: 10, flexShrink: 0 }}></span>
                          <div>
                            <div className="small fw-bold">{fund.fund_name}</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{fund.tr_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="small">{fund.asset_class}</td>
                      <td className="small">{fund.ter != null ? `${parseFloat(fund.ter).toFixed(2)}%` : '—'}</td>
                      <td>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="0" max="100"
                            value={fund.weight || ''}
                            placeholder="0"
                            onChange={(e) => updateFundWeight(idx, e.target.value)}
                            style={{ paddingRight: '28px' }}
                          />
                          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-color)', fontSize: '13px', pointerEvents: 'none' }}>%</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <div style={{ width: '100%', height: 6, backgroundColor: 'var(--light-color)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(fund.weight || 0, 100)}%`, height: '100%', backgroundColor: BAR_COLOURS[colourIdx % BAR_COLOURS.length], borderRadius: 3, transition: 'width 0.3s ease' }}></div>
                        </div>
                      </td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm" onClick={() => removeFund(idx)} title="Remove" style={{ color: '#dc3545', padding: '2px 6px' }}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {fundEntries.length === 0 && (
          <div className="text-center py-3 mb-2" style={{ backgroundColor: 'var(--light-color)', borderRadius: '8px' }}>
            <p className="text-muted mb-0 small">No individual funds added yet</p>
          </div>
        )}

        <div className="text-center mb-1">
          <button type="button" className="btn btn-sm text-white" style={{ backgroundColor: 'var(--primary-color)' }} onClick={() => setShowFundSearch(true)}>
            <i className="bi bi-plus-circle me-1"></i>Add Fund
          </button>
        </div>
      </div>

      {/* ═══ Asset Classes ═══ */}
      <div className="soa-section-banner">
        <i className="bi bi-grid-3x3"></i>
        Asset Classes
      </div>

      <div className="mb-3">
        {acRows.length > 0 && (
          <div className="table-responsive mb-2">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr style={{ backgroundColor: 'var(--light-color)' }}>
                  <th style={{ width: '50%' }}>Asset Class</th>
                  <th style={{ width: '25%' }}>Allocation (%)</th>
                  <th style={{ width: '15%' }} className="text-center">Weight</th>
                  <th style={{ width: '10%' }}></th>
                </tr>
              </thead>
              <tbody>
                {acRows.map((row, idx) => {
                  const colourIdx = modelRows.length + fundEntries.length + idx;
                  const ac = assetClasses.find(a => a.name === row.name);
                  return (
                    <tr key={row.name}>
                      <td>
                        <div className="d-flex align-items-center">
                          <span style={{ width: 4, height: 28, borderRadius: 2, backgroundColor: BAR_COLOURS[colourIdx % BAR_COLOURS.length], display: 'inline-block', marginRight: 10, flexShrink: 0 }}></span>
                          <select
                            className="form-select form-select-sm"
                            value={row.name}
                            onChange={(e) => updateAcSelection(row.name, e.target.value)}
                            style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none', paddingLeft: 0 }}
                          >
                            {assetClasses
                              .filter(a => a.name === row.name || !selectedAcNames.has(a.name))
                              .map(a => (
                                <option key={a.name} value={a.name}>
                                  {a.name.replace(/([A-Z])/g, ' $1').trim()}
                                  {a.expected_return != null ? ` — Return ${(a.expected_return * 100).toFixed(1)}%` : ''}
                                </option>
                              ))}
                          </select>
                        </div>
                      </td>
                      <td>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="0" max="100"
                            value={row.weight || ''}
                            placeholder="0"
                            onChange={(e) => updateAcWeight(row.name, e.target.value)}
                            style={{ paddingRight: '28px' }}
                          />
                          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-color)', fontSize: '13px', pointerEvents: 'none' }}>%</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <div style={{ width: '100%', height: 6, backgroundColor: 'var(--light-color)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(row.weight || 0, 100)}%`, height: '100%', backgroundColor: BAR_COLOURS[colourIdx % BAR_COLOURS.length], borderRadius: 3, transition: 'width 0.3s ease' }}></div>
                        </div>
                      </td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm" onClick={() => removeAcRow(row.name)} title="Remove" style={{ color: '#dc3545', padding: '2px 6px' }}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {acRows.length === 0 && (
          <div className="text-center py-3 mb-2" style={{ backgroundColor: 'var(--light-color)', borderRadius: '8px' }}>
            <p className="text-muted mb-0 small">No direct asset class allocations added yet</p>
          </div>
        )}

        <div className="text-center mb-1">
          <button type="button" className="btn btn-sm text-white" style={{ backgroundColor: 'var(--primary-color)' }} onClick={addAcRow}
            disabled={selectedAcNames.size >= assetClasses.length}>
            <i className="bi bi-plus-circle me-1"></i>Add Asset Class
          </button>
        </div>
      </div>

      {/* ═══ Total & Calculate ═══ */}
      <div className={`alert ${isValidTotal ? 'alert-success' : 'alert-warning'} mb-3 py-2`}>
        <div className="d-flex justify-content-between align-items-center">
          <span className="fw-bold">Total Allocation:</span>
          <span className="fs-5 fw-bold">{tw.toFixed(1)}%</span>
        </div>
        {tw > 100 && <small className="d-block mt-1">Total exceeds 100%</small>}
        {tw > 0 && tw < 100 && <small className="d-block mt-1">Total must equal 100%</small>}
      </div>

      <button
        type="button"
        className="btn btn-primary w-100 mb-3"
        onClick={handleCalculateBlend}
        disabled={disabled || blendLoading || !isValidTotal || tw === 0}
        style={{ backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)' }}
      >
        {blendLoading ? (
          <><span className="spinner-border spinner-border-sm me-2"></span>Calculating...</>
        ) : (
          <><i className="bi bi-calculator me-1"></i>Calculate Blend</>
        )}
      </button>

      {/* ═══ Blend Results ═══ */}
      {blendResults && (
        <div className="p-3 rounded mb-3" style={{ border: '1px solid var(--border-color)', backgroundColor: '#fff' }}>
          <h6 className="mb-3" style={{ color: 'var(--primary-color)' }}>
            <i className="bi bi-bar-chart-line me-2"></i>Blend Results
          </h6>
          <div className="row mb-3">
            <div className="col-md-3 text-center">
              <div className="small text-muted">Expected Return</div>
              <div className="fw-bold" style={{ color: 'var(--primary-color)' }}>
                {blendResults.expected_return != null ? `${(blendResults.expected_return * 100).toFixed(2)}%` : '—'}
              </div>
            </div>
            <div className="col-md-3 text-center">
              <div className="small text-muted">Volatility</div>
              <div className="fw-bold" style={{ color: 'var(--primary-color)' }}>
                {blendResults.volatility != null ? `${(blendResults.volatility * 100).toFixed(2)}%` : '—'}
              </div>
            </div>
            <div className="col-md-3 text-center">
              <div className="small text-muted">Growth Exposure</div>
              <div className="fw-bold" style={{ color: 'var(--primary-color)' }}>
                {blendResults.growth_exposure != null ? `${(blendResults.growth_exposure * 100).toFixed(0)}%` : '—'}
              </div>
            </div>
            <div className="col-md-3 text-center">
              <div className="small text-muted">Income Yield</div>
              <div className="fw-bold" style={{ color: 'var(--primary-color)' }}>
                {blendResults.income != null ? `${(blendResults.income * 100).toFixed(2)}%` : '—'}
              </div>
            </div>
          </div>

          {/* Growth/Defence bar */}
          {blendResults.growth_exposure != null && (
            <div className="mb-3">
              <div className="small text-muted mb-1">Asset Allocation</div>
              <div style={{ display: 'flex', height: '32px', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.round(blendResults.growth_exposure * 100)}%`,
                  backgroundColor: 'var(--primary-color)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 600,
                }}>
                  Growth {Math.round(blendResults.growth_exposure * 100)}%
                </div>
                <div style={{
                  width: `${100 - Math.round(blendResults.growth_exposure * 100)}%`,
                  backgroundColor: 'var(--info-color)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 600,
                }}>
                  Defence {100 - Math.round(blendResults.growth_exposure * 100)}%
                </div>
              </div>
            </div>
          )}

          {/* Lookthrough table */}
          {blendResults.asset_class_lookthrough && (
            <div>
              <div className="small text-muted mb-1">Asset Class Lookthrough</div>
              <div className="table-responsive">
                <table className="table table-sm" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
                      <th>Asset Class</th>
                      <th className="text-end">Allocation</th>
                      <th style={{ width: '40%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(blendResults.asset_class_lookthrough)
                      .filter(([, v]) => v > 0.001)
                      .sort(([, a], [, b]) => b - a)
                      .map(([ac, pct]) => (
                        <tr key={ac}>
                          <td>{ac}</td>
                          <td className="text-end">{(pct * 100).toFixed(1)}%</td>
                          <td>
                            <div style={{ height: '14px', borderRadius: '3px', overflow: 'hidden', backgroundColor: '#e9ecef' }}>
                              <div style={{ width: `${Math.min(pct * 100, 100)}%`, height: '100%', backgroundColor: 'var(--primary-color)' }}></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {blendResults.icr != null && (
            <div className="small text-muted">
              Blended ICR: <strong>{blendResults.icr.toFixed(4)}%</strong>
            </div>
          )}
        </div>
      )}

      <FundSearchModal
        show={showFundSearch}
        onClose={() => setShowFundSearch(false)}
        onSelectFund={addFund}
      />

      {/* ═══ RATIONALE ═══ */}
      <div className="soa-section-banner">
        <i className="bi bi-chat-left-text"></i>
        Basis for Advice
      </div>

      <TemplateTextField
        fieldKey="strategy.rationale"
        label="Rationale — why is this strategy appropriate? *"
        value={data.rationale}
        onChange={(val) => handleChange('rationale', val)}
        placeholder="Explain why this portfolio and strategy is appropriate for the client given their objectives, risk profile, and circumstances..."
        rows={5}
        disabled={disabled}
      />

      <div className="soa-form-group">
        <label>Implementation Notes</label>
        <textarea
          className="form-control"
          value={data.implementationNotes}
          onChange={(e) => handleChange('implementationNotes', e.target.value)}
          placeholder="Any specific notes about how the strategy will be implemented (e.g. platform, transition approach, timing)..."
          rows={3}
        />
      </div>

      <div className="soa-step-nav">
        <button className="btn btn-outline-secondary" onClick={prevStep}>
          <i className="bi bi-arrow-left me-1"></i>Back
        </button>
        <button className="btn btn-primary" onClick={nextStep}>
          Next: Retirement Projection <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </fieldset>
  );
};

export default StepStrategy;
